/**
 * Weight Comparison Component
 *
 * Shows total weight per workout vs average for selected workout type
 * For Cardio: shows duration instead of weight
 * Simple list format with +/- indicators
 */

import { useState } from 'react';
import './WeightComparison.css';

type WorkoutType = 'Push' | 'Pull' | 'Leg' | 'Arms' | 'Cardio' | 'Other';

interface WorkoutData {
  id: string;
  date: string;
  value: number; // weight (lbs) for strength, duration (minutes) for cardio
}

interface TypeData {
  workouts: WorkoutData[];
  average: number;
  isCardio?: boolean;
}

interface WeightComparisonProps {
  data: Record<WorkoutType, TypeData>;
}

const WORKOUT_TYPES: WorkoutType[] = ['Push', 'Pull', 'Leg', 'Arms', 'Cardio', 'Other'];

export function WeightComparison({ data }: WeightComparisonProps): JSX.Element {
  const [selectedType, setSelectedType] = useState<WorkoutType>('Push');

  const typeData = data[selectedType];
  const hasData = typeData && typeData.workouts.length > 0;
  const isCardio = selectedType === 'Cardio';

  const formatValue = (value: number): string => {
    if (isCardio) {
      return value + ' min';
    }
    return value.toLocaleString() + ' lbs';
  };

  const formatDiff = (diff: number): string => {
    const sign = diff >= 0 ? '+' : '';
    if (isCardio) {
      return sign + diff + ' min';
    }
    return sign + diff.toLocaleString();
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="weight-comparison">
      <div className="weight-comparison__tabs">
        {WORKOUT_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            className={`weight-comparison__tab ${selectedType === type ? 'weight-comparison__tab--active' : ''}`}
            onClick={() => setSelectedType(type)}
          >
            {type}
          </button>
        ))}
      </div>

      <div className="weight-comparison__content">
        {!hasData ? (
          <div className="weight-comparison__empty">
            No {selectedType} workouts recorded
          </div>
        ) : (
          <>
            <div className="weight-comparison__average">
              Avg: {formatValue(typeData.average)}
            </div>

            <div className="weight-comparison__list">
              {typeData.workouts.map((workout) => {
                const diff = workout.value - typeData.average;
                const isAbove = diff >= 0;

                return (
                  <div
                    key={workout.id}
                    className={`weight-comparison__item ${isAbove ? 'weight-comparison__item--above' : 'weight-comparison__item--below'}`}
                  >
                    <span className="weight-comparison__date">
                      {formatDate(workout.date)}
                    </span>
                    <span className="weight-comparison__weight">
                      {formatValue(workout.value)}
                    </span>
                    <span className={`weight-comparison__diff ${isAbove ? 'weight-comparison__diff--positive' : 'weight-comparison__diff--negative'}`}>
                      ({formatDiff(diff)})
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
